/* eslint-disable indent */
'use client';

import { useState, useEffect } from 'react';
import Image from 'next/image';
import { motion, AnimatePresence, useAnimate } from 'framer-motion';

import { HashLoader } from 'react-spinners';

export default function ImageLoader({
  src,
  alt,
  fill = false,
  width,
  height,
  className = '',
  objectFit = 'cover',
  priority = false,
  loaderType = 'pulse', // "pulse", "shimmer", "blur", "dots", or "x"
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [imgSrc, setImgSrc] = useState(src);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const mainColor = isDarkMode ? '#38F5EF' : '#FE2A53';
  const [scope, animate] = useAnimate();
  const [show, setShow] = useState(true);

  useEffect(() => {
    if (loaderType === 'custom') {
      const animateLoader = async () => {
        animate(
          [
            ['.x1', { pathLength: 1, pathOffset: 1 }],
            ['.x2', { pathLength: 1, pathOffset: 1 }],
          ],
          { duration: 0.2, ease: 'linear', repeat: Infinity },
        );
      };

      animateLoader();

      // Hide loader after 3 seconds
      const timer = setTimeout(() => {
        setShow(false);
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [animate, loaderType]);

  // Check for dark mode after component mounts
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Initial check
      setIsDarkMode(document.documentElement.classList.contains('dark'));

      // Set up observer to detect theme changes
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.attributeName === 'class') {
            setIsDarkMode(document.documentElement.classList.contains('dark'));
          }
        });
      });

      observer.observe(document.documentElement, { attributes: true });

      return () => observer.disconnect();
    }
  }, []);

  // Reset loading state if src changes
  useEffect(() => {
    setLoading(true);
    setError(false);
    setImgSrc(src);
  }, [src]);

  // Handle image load complete
  const handleLoadComplete = () => {
    setLoading(false);
  };

  // Handle image load error
  const handleError = () => {
    console.error('Error loading image:', imgSrc);
    setError(true);
    setLoading(false);
    // Fall back to placeholder
    setImgSrc('/placeholder.svg?height=400&width=600');
  };

  // Shimmer effect
  const shimmer = (w, h) => `
    <svg width="${w}" height="${h}" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
      <defs>
        <linearGradient id="g">
          <stop stopColor="#f0f0f0" offset="20%" />
          <stop stopColor="#f8f8f8" offset="50%" />
          <stop stopColor="#f0f0f0" offset="70%" />
        </linearGradient>
      </defs>
      <rect width="${w}" height="${h}" fill="#f0f0f0" />
      <rect id="r" width="${w}" height="${h}" fill="url(#g)" />
      <animate xlinkHref="#r" attributeName="x" from="-${w}" to="${w}" dur="1s" repeatCount="indefinite"  />
    </svg>
  `;

  const toBase64 = (str) =>
    typeof window === 'undefined'
      ? Buffer.from(str).toString('base64')
      : window.btoa(str);

  // Determine which loader to show
  const renderLoader = () => {
    if (loaderType === 'custom' && show) {
      return (
        <div className='fixed inset-0 z-[10000] flex items-center justify-center bg-white'>
          <svg
            ref={scope}
            width='140.033241mm'
            height='120.361317mm'
            viewBox='-15 0 93.033241 70.361316'
          >
            <motion.path
              className='x1'
              initial={{ pathLength: 0.05, pathOffset: 0.05 }}
              d='M 10,10 L 50,50'
              stroke='#13d0e1'
              strokeWidth='2'
            />
            <motion.path
              className='x2'
              initial={{ pathLength: 0.05, pathOffset: 0.05 }}
              d='M 50,10 L 10,50'
              stroke={mainColor}
              strokeWidth='2'
            />
          </svg>
        </div>
      );
    }
    switch (loaderType) {
      case 'shimmer':
        return (
          <div
            className={`absolute inset-0 bg-gray-200 dark:bg-gray-800 overflow-hidden ${className}`}
            style={{
              backgroundImage: `url(data:image/svg+xml;base64,${toBase64(
                shimmer(width || 700, height || 475),
              )})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
        );
      case 'blur':
        return (
          <div className='absolute inset-0 backdrop-blur-sm bg-gray-200/30 dark:bg-gray-800/30'>
            <div className='absolute inset-0 flex items-center justify-center'>
              <div className='w-16 h-16 rounded-full bg-white/80 dark:bg-black/80 flex items-center justify-center'>
                <svg
                  className='w-8 h-8 text-primarycolor animate-spin'
                  xmlns='http://www.w3.org/2000/svg'
                  fill='none'
                  viewBox='0 0 24 24'
                >
                  <circle
                    className='opacity-25'
                    cx='12'
                    cy='12'
                    r='10'
                    stroke='currentColor'
                    strokeWidth='4'
                  ></circle>
                  <path
                    className='opacity-75'
                    fill='currentColor'
                    d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                  ></path>
                </svg>
              </div>
            </div>
          </div>
        );
      case 'dots':
        return (
          <div className='absolute inset-0 bg-gray-200 dark:bg-gray-800 flex items-center justify-center'>
            <div className='flex space-x-2'>
              {[0, 1, 2].map((i) => (
                <motion.div
                  key={i}
                  className='w-3 h-3 rounded-full bg-primarycolor dark:bg-secondarycolor'
                  initial={{ scale: 0.8, opacity: 0.5 }}
                  animate={{
                    scale: [0.8, 1.2, 0.8],
                    opacity: [0.5, 1, 0.5],
                  }}
                  transition={{
                    duration: 1,
                    repeat: Number.POSITIVE_INFINITY,
                    delay: i * 0.2,
                    ease: 'easeInOut',
                  }}
                />
              ))}
            </div>
          </div>
        );
      case 'x':
        return (
          <div className='absolute inset-0 bg-gray-200/50 dark:bg-gray-800/50 backdrop-blur-sm flex items-center justify-center'>
            <div className='relative w-16 h-16 flex items-center justify-center'>
              {/* X Logo Animation */}
              <motion.div
                className='absolute'
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{
                  opacity: [0, 1, 0],
                  scale: [0.5, 1.2, 0.5],
                  rotateZ: [0, 360, 0],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              >
                <svg
                  width='40'
                  height='40'
                  viewBox='0 0 24 24'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M18 6L6 18M6 6L18 18'
                    stroke={isDarkMode ? '#38F5EF' : '#FE2A53'}
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </motion.div>

              {/* Pulsing Circle */}
              <motion.div
                className='absolute w-full h-full rounded-full'
                style={{
                  border: `2px solid ${isDarkMode ? '#38F5EF' : '#FE2A53'}`,
                  boxShadow: `0 0 10px ${isDarkMode ? '#38F5EF' : '#FE2A53'}`,
                }}
                animate={{
                  opacity: [0.2, 0.8, 0.2],
                  scale: [0.8, 1, 0.8],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              />
            </div>
          </div>
        );
      case 'hash':
        return (
          <div className='absolute inset-0 bg-gray-200/50 dark:bg-gray-800/50 backdrop-blur-sm flex items-center justify-center'>
            <HashLoader
              color={isDarkMode ? '#38F5EF' : '#FE2A53'} // Use state variable instead of checking document
              loading={loading}
              size={50}
              speedMultiplier={1.5}
            />
          </div>
        );
      case 'pulse':
      default:
        return (
          <motion.div
            className='absolute inset-0 bg-gray-200 dark:bg-gray-800'
            animate={{
              opacity: [0.7, 0.9, 0.7],
            }}
            transition={{
              duration: 1.5,
              repeat: Number.POSITIVE_INFINITY,
              ease: 'easeInOut',
            }}
          />
        );
    }
  };

  return (
    <div
      className={`${fill ? 'md:relative w-full h-full' : 'w-full h-full'} overflow-hidden ${className}`}
    >
      {/* Loader */}
      <AnimatePresence>
        {loading && (
          <motion.div
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className='absolute inset-0 z-10'
          >
            {renderLoader()}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Actual Image */}
      <div
        // className={`${
        //   loading ? 'opacity-0' : 'opacity-100'
        // } transition-opacity duration-300`}
        className={`relative w-full h-full ${loading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}
      >
        {fill ? (
          <Image
            src={imgSrc || '/placeholder.svg'}
            alt={alt}
            fill
            className={`object-${objectFit}`}
            onLoad={handleLoadComplete}
            onError={handleError}
            priority={priority}
            sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw'
            unoptimized={true}
          />
        ) : (
          <Image
            src={imgSrc || '/placeholder.svg'}
            alt={alt}
            width={width || 500}
            height={height || 300}
            className={`object-${objectFit}`}
            onLoad={handleLoadComplete}
            onError={handleError}
            priority={priority}
            sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw'
            unoptimized={true}
          />
        )}
      </div>

      {/* <div
        className={`relative w-full h-64 ${loading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}
      >
        <Image
          fill
          src={imgSrc || '/placeholder.svg'}
          alt={alt}
          className={`object-${objectFit}`}
          onLoad={handleLoadComplete}
          onError={handleError}
          priority={priority}
          sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw'
          unoptimized={true}
        />
      </div> */}
    </div>
  );
}
